
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Device } from '@/components/device-health/gis-map/types';
import { AlertTriangle, Info } from 'lucide-react';

export interface AiPredictionData {
  title: string;
  description: string;
  icon: React.ReactNode;
  accuracy: string;
  impact: string;
  deviceId?: string; // Added deviceId for device-specific predictions
  alert?: string; // Related alert message
  recommendedAction?: string; // Recommended action based on prediction
}

interface AiPredictionsListProps {
  predictions: AiPredictionData[];
  selectedDeviceId?: string;
  deviceData?: Device | null;
  assetTypeFilter?: string | null;
  priorityFilter?: string | null;
  facilityFilter?: string | null;
}

const AiPredictionsList: React.FC<AiPredictionsListProps> = ({ 
  predictions,
  selectedDeviceId,
  deviceData,
  assetTypeFilter,
  priorityFilter,
  facilityFilter
}) => {
  // Filter predictions if a device is selected
  const filteredPredictions = selectedDeviceId
    ? predictions.filter(pred => !pred.deviceId || pred.deviceId === selectedDeviceId)
    : predictions;

  // Create dynamic predictions based on device alerts if needed
  const dynamicPredictions = React.useMemo(() => {
    if (!deviceData || !selectedDeviceId || filteredPredictions.length > 0) {
      return filteredPredictions;
    }
    
    // Create a default prediction if no predictions exist for this device
    return [{
      title: `AI Analysis for ${deviceData.name}`,
      description: `Based on current telemetry data, this device is operating within normal parameters. No immediate maintenance action required.`,
      icon: <Info className="h-5 w-5 text-energy-blue" />,
      accuracy: "98%",
      impact: "Low",
      deviceId: selectedDeviceId,
      recommendedAction: "Continue with regularly scheduled maintenance."
    }];
  }, [filteredPredictions, deviceData, selectedDeviceId]);

  const getTitle = (title: string, prediction: AiPredictionData) => {
    if (deviceData && selectedDeviceId) {
      return `${title} for ${deviceData.name}`;
    }
    return title;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {dynamicPredictions.length > 0 ? (
        dynamicPredictions.map((prediction, index) => (
          <Card key={index} className="overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-start">
                <div className="bg-energy-blue/10 p-3 rounded-full mr-4">
                  {prediction.icon}
                </div>
                <div>
                  <h3 className="font-medium">{getTitle(prediction.title, prediction)}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {prediction.description}
                  </p>
                  
                  {prediction.alert && (
                    <div className="mt-2 flex items-start">
                      <AlertTriangle className="h-4 w-4 text-amber-500 mr-1 mt-0.5" />
                      <p className="text-xs text-amber-600 dark:text-amber-400">
                        {prediction.alert}
                      </p>
                    </div>
                  )}
                  
                  {prediction.recommendedAction && (
                    <p className="text-xs bg-gray-50 dark:bg-gray-800 p-2 mt-2 rounded-sm">
                      <span className="font-medium">Recommendation:</span> {prediction.recommendedAction}
                    </p>
                  )}
                  
                  <div className="mt-4 flex justify-between">
                    <div className="text-sm">
                      <span className="font-medium">Accuracy:</span> {prediction.accuracy}
                    </div>
                    <div className="text-sm text-energy-blue">
                      <span className="font-medium">Impact:</span> {prediction.impact}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))
      ) : (
        <div className="col-span-2 p-6 text-center border rounded-lg">
          <p className="text-muted-foreground">No AI predictions available for this device</p>
        </div>
      )}
    </div>
  );
};

export default AiPredictionsList;
